<template>
    <popup 
        v-model:show="showValue" 
        round 
        class="tip-popup"
        :overlay-style="overlayStyle"
        :closeable="hasClose"
        teleport="body"
        :close-on-click-overlay="closeOnClickOverlay"
        @click-overlay="handleClickOverlay"
        @closed="handleClosed"
        :z-index="zIndex"
    >
        <template v-if="type === 'create'">
            <img class="tip-icon create-icon" src="@/assets/img/home/create-farm-icon.png" alt="" />
            <div class="tip-text">
                <div v-for="(line, index) in textLines" :key="index">{{ line }}</div>
            </div>
        </template>
        <template v-else-if="type === 'success'">
            <img class="tip-icon success-icon" src="@/assets/img/home/right.png" alt="" />
            <slot name="default"></slot>
            <div class="tip-text success-text" :class="{pmzd:font, 'no-bottom': hideBtn}">
                {{ text }} <span class="highlight-text">{{ highlightText }}</span>
                <div class="text2">{{ text2 }}</div>
            </div>
        </template>
        <template v-else-if="type === 'executeSuccess'">
            <img class="tip-icon success-icon" src="@/assets/img/home/right.png" alt="" />
            <div class="tip-text execute-success-text" :class="{ 'no-bottom': hideBtn && !noticeText }">
                <div class="execute-success-title">{{ text }}</div>
                <div v-if="text2" class="execute-success-subtitle">{{ text2 }}</div>
            </div>
            <div v-if="noticeText" class="tip-notice">{{ noticeText }}</div>
        </template>
        <template v-else>
            <img class="tip-icon success-icon" src="@/assets/img/home/create-farm-icon.png" alt="" />
            <div class="tip-text success-text">{{ text }} <span class="highlight-text">{{ highlightText }}</span> {{ text2 }}</div>
        </template>
        <slot name="footer"></slot>
        <div class="tip-btn" v-if="!hideBtn" @click.stop="handleBtnClick">
            {{ buttonText || (type === "create" ? "去创建农场" : "我知道了") }}
        </div>
    </popup>
</template>

<script setup>
import { Popup } from "vant";
import { computed } from "vue";

const props = defineProps({
    // 控制弹窗显示/隐藏
    show: {
        type: Boolean,
        default: false,
    },
    // 类型：'create' 或 'success' 或 'warning'，决定显示哪个图标
    type: {
        type: String,
        default: "create",
    },
    // 提示文字内容
    text: {
        type: [String, Array],
        default: "",
    },
    // 按钮文字，如果不传则根据type自动决定
    buttonText: {
        type: String,
        default: "",
    },
    // 遮罩层样式
    overlayStyle: {
        type: Object,
        default: () => ({}),
    },
    // 是否在点击遮罩层后关闭弹窗
    closeOnClickOverlay: {
        type: Boolean,
        default: true,
    },
    // 高亮文字
    highlightText: {
        type: String,
        default: "",
    },
    // 第二行文字
    text2: {
        type: String,
        default: "",
    },
    // 底部提示说明（executeSuccess 类型）
    noticeText: {
        type: String,
        default: "",
    },
    // 是否可以关闭
    hasClose: {
        type: Boolean,
        default: false,
    },
    // 遮罩层z-index
    zIndex: {
        type: Number,
        default: 2003,
    },
    font: {
        type:Boolean,
        default:false
    },
    hideBtn: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:show", "confirm", "handleClickOverlay", "closedPopup"]);

// 处理v-model双向绑定
const showValue = computed({
    get: () => props.show,
    set: (value) => emit("update:show", value),
});

// 将文字内容转换为数组，支持多行显示
const textLines = computed(() => {
    if (Array.isArray(props.text)) {
        return props.text;
    }
    if (typeof props.text === "string") {
        return props.text.split("\n").filter((line) => line.trim());
    }
    return [];
});

const handleBtnClick = () => {
    emit("confirm");
    if(props.buttonText !== "分享微信"){
        emit("update:show", false);
    }
};

const handleClickOverlay = () => {
    emit("handleClickOverlay");
    if(props.closeOnClickOverlay){
        emit("update:show", false);
    }
};

const handleClosed = () => {
    emit("closedPopup");
};
</script>

<style scoped lang="scss">
.tip-popup {
    z-index: 10000 !important;
    width: 100%;
    padding: 28px 20px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .tip-icon {
        margin-bottom: 12px;
        &.create-icon {
            width: 40px;
            height: 40px;
        }
        &.success-icon {
            width: 68px;
            height: 68px;
        }
    }
    .tip-text {
        font-size: 18px;
        font-weight: 500;
        margin-bottom: 32px;
        text-align: center;
        &.no-bottom {
            margin-bottom: 6px;
        }
        &.success-text {
            font-size: 24px;
            font-weight: 400;
        }
        .highlight-text {
            color: #2199f8;
        }
        &.pmzd{
            font-family: 'PangMenZhengDao';
        }
        .text2 {
            padding-top: 10px;
            font-size: 20px;
            color: rgba(0, 0, 0, 0.5);
        }

        &.execute-success-text {
            width: 100%;
            margin-bottom: 16px;

            .execute-success-title,
            .execute-success-subtitle {
                font-size: 24px;
                font-weight: 400;
            }

            .execute-success-subtitle {
                padding-top: 4px;
            }
        }
    }

    .tip-notice {
        width: 100%;
        box-sizing: border-box;
        padding: 2px 10px;
        border-radius: 5px;
        background: rgba(255, 106, 106, 0.1);
        color: #FF4A4A;
        font-size: 15px;
        text-align: left;
    }

    .tip-btn {
        width: 100%;
        box-sizing: border-box;
        padding: 8px;
        border-radius: 25px;
        font-size: 16px;
        background: #2199f8;
        color: #fff;
        text-align: center;
        cursor: pointer;
    }
}
</style>

